import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { toast } from 'sonner';
import { 
  Globe, 
  Send,
  Eye,
  RefreshCw,
  Loader2,
  Settings,
  CheckCircle,
  ExternalLink,
  Archive,
  ArchiveRestore,
  FileText,
  Infinity,
  MapPin,
  Search,
  Copy
} from 'lucide-react';
import axios from 'axios';
import WebsiteManageDialog from '../../components/admin/WebsiteManageDialog';

// Format category for display
const formatCategory = (category) => {
  if (!category) return 'Business';
  return category
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

// Format address with proper capitalization
const formatAddress = (address) => {
  if (!address) return '';
  
  // Handle UK postcodes - they should be fully uppercase
  let result = address.replace(
    /\b([A-Za-z]{1,2}[0-9][0-9A-Za-z]?\s*[0-9][A-Za-z]{2})\b/gi,
    (match) => match.toUpperCase()
  );
  
  // Capitalize each word
  result = result
    .split(' ')
    .map(word => {
      if (word === word.toUpperCase() && word.length <= 4) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
  
  return result
    .replace(/\bUk\b/g, 'UK')
    .replace(/\bNw(\d)/gi, 'NW$1')
    .replace(/\bSw(\d)/gi, 'SW$1')
    .replace(/\bSe(\d)/gi, 'SE$1')
    .replace(/\bNe(\d)/gi, 'NE$1')
    .replace(/\bEc(\d)/gi, 'EC$1')
    .replace(/\bWc(\d)/gi, 'WC$1');
};

// Extract postcode from address for Google search
const extractPostcode = (address) => {
  if (!address) return '';
  const match = address.match(/\b([A-Za-z]{1,2}[0-9][0-9A-Za-z]?\s*[0-9][A-Za-z]{2})\b/i);
  return match ? match[1].toUpperCase() : '';
};

// Build Google search URL for finding business contact/Facebook
const buildGoogleSearchUrl = (businessName, address) => {
  const postcode = extractPostcode(address);
  const searchQuery = postcode 
    ? `${businessName} ${postcode}`
    : businessName;
  return `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
};

// Extract area/town from address for FB message
const extractArea = (address) => {
  if (!address) return 'the area';
  const parts = address.split(',').map(p => p.trim());
  if (parts.length >= 2) {
    const townPart = parts[parts.length - 2] || parts[0];
    return townPart.replace(/\b[A-Z]{1,2}[0-9][0-9A-Z]?\s*[0-9][A-Z]{2}\b/gi, '').trim() || 'the area';
  }
  return 'the area';
};

// Generate Facebook/Instagram message for initial outreach (before sending email)
const generateDMInitial = (businessName, address) => {
  return `Hey ${businessName}, I figured I would drop a message on socials as I know lots of people think emails are spam which is very fair haha! I build websites for independent companies and they're just on basic subscription models to keep the price down - let me know if you'd like a preview link! I can make adjustments too 😊`;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GeneratedWebsites = () => {
  const { token } = useAuth();
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [sendEmailDialog, setSendEmailDialog] = useState(false);
  const [sendingTo, setSendingTo] = useState(null);
  const [clientEmail, setClientEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingFacebook, setSendingFacebook] = useState(null);
  // New manage dialog state
  const [manageDialog, setManageDialog] = useState(false);
  const [managingWebsite, setManagingWebsite] = useState(null);
  const [archiving, setArchiving] = useState(null);
  const [changeLogDialog, setChangeLogDialog] = useState(false);
  const [changeLogWebsite, setChangeLogWebsite] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchWebsites();
  }, []);

  const fetchWebsites = async () => {
    try {
      const response = await axios.get(`${API}/websites`, { headers });
      // Filter out preview_sent and ordered - they have their own pages
      const draftWebsites = response.data.filter(w => w.status === 'draft');
      setWebsites(draftWebsites);
    } catch (error) {
      console.error('Failed to fetch websites:', error);
      toast.error('Failed to load websites');
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (website) => {
    setArchiving(website.id);
    try {
      const response = await axios.patch(
        `${API}/websites/${website.id}/archive`,
        {},
        { headers }
      );
      toast.success(response.data.message);
      fetchWebsites();
    } catch (error) {
      toast.error('Failed to update archive status');
    } finally {
      setArchiving(null);
    }
  };

  const handleSendPreview = async () => {
    if (!clientEmail || !sendingTo) return;
    
    setSendingEmail(true);
    try {
      await axios.post(
        `${API}/websites/send-preview`,
        { website_id: sendingTo.id, client_email: clientEmail },
        { headers }
      );
      toast.success('Preview sent successfully!');
      setSendEmailDialog(false);
      setClientEmail('');
      setSendingTo(null);
      fetchWebsites();
    } catch (error) {
      toast.error('Failed to send preview');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleMakeLive = async (websiteId) => {
    try {
      await axios.patch(`${API}/websites/${websiteId}/go-live`, {}, { headers });
      toast.success('Website is now live!');
      fetchWebsites();
    } catch (error) {
      toast.error('Failed to make website live');
    }
  };

  const handleSentViaFacebook = async (website) => {
    setSendingFacebook(website.id);
    try {
      await axios.post(`${API}/websites/${website.id}/sent-via-facebook`, {}, { headers });
      toast.success('Marked as sent via Meta!');
      fetchWebsites();
    } catch (error) {
      toast.error('Failed to mark as sent');
    } finally {
      setSendingFacebook(null);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: { variant: 'secondary', label: 'Draft', color: 'bg-gray-500/10 text-gray-400' },
      preview_sent: { variant: 'default', label: 'Preview Sent', color: 'bg-purple-500/10 text-purple-400' },
      ordered: { variant: 'default', label: 'Ordered', color: 'bg-blue-500/10 text-blue-400' },
      live: { variant: 'default', label: 'Live', color: 'bg-green-500/10 text-green-400' }
    };
    const config = variants[status] || { variant: 'secondary', label: status, color: 'bg-gray-500/10 text-gray-400' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  // Filter websites based on archive status
  const filteredWebsites = websites.filter(w => 
    showArchived ? w.archived === true : w.archived !== true
  );
  
  // Count archived websites
  const archivedCount = websites.filter(w => w.archived === true).length;
  const activeCount = websites.filter(w => w.archived !== true).length;

  return (
    <div className="space-y-6" data-testid="generated-websites">
      <Card className="bg-card/50 border-white/5">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {showArchived ? (
                <Archive className="w-5 h-5 text-muted-foreground" />
              ) : (
                <Globe className="w-5 h-5 text-primary" />
              )}
              {showArchived ? `Archived Websites (${archivedCount})` : `Generated Websites (${activeCount})`}
            </CardTitle>
            <CardDescription>
              {showArchived 
                ? 'View and restore your archived websites'
                : 'Manage, preview, and send your generated websites to clients'
              }
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button 
              variant={showArchived ? "default" : "outline"} 
              size="sm" 
              onClick={() => setShowArchived(!showArchived)}
              data-testid="toggle-archived"
            >
              {showArchived ? (
                <>
                  <Globe className="w-4 h-4 mr-2" />
                  Show Active
                </>
              ) : (
                <>
                  <Archive className="w-4 h-4 mr-2" />
                  Show Archived {archivedCount > 0 && `(${archivedCount})`}
                </>
              )}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchWebsites}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {filteredWebsites.length === 0 ? (
            <div className="py-16 text-center">
              {showArchived ? (
                <>
                  <Archive className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-medium mb-2">No archived websites</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Websites you archive will appear here.
                  </p>
                </>
              ) : (
                <>
                  <Globe className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-medium mb-2">No websites generated yet</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Go to &quot;To Action&quot; and generate websites for your favourited businesses.
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredWebsites.map((website) => (
                <div
                  key={website.id}
                  className={`bg-background/50 border rounded-xl p-5 ${showArchived ? 'border-white/5 opacity-75' : 'border-white/5'}`}
                  data-testid={`website-${website.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-lg">{website.business_name}</h4>
                        {getStatusBadge(website.status)}
                        {website.archived && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-500/10 text-orange-400">
                            Archived
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground space-y-1">
                        {/* Address and Category - matching To Action page */}
                        {website.formatted_address && (
                          <p className="flex items-center gap-1.5">
                            <MapPin className="w-3 h-3" /> {formatAddress(website.formatted_address)}
                          </p>
                        )}
                        {website.primary_type && (
                          <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium mt-1">
                            {formatCategory(website.primary_type)}
                          </span>
                        )}
                        {/* Google Search link for finding Facebook/contact */}
                        <a 
                          href={buildGoogleSearchUrl(website.business_name, website.formatted_address)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-blue-400 hover:underline mt-1"
                        >
                          <Search className="w-3 h-3" />
                          Search on Google
                        </a>
                        {/* Copy DM for initial outreach */}
                        <button
                          onClick={() => {
                            const message = generateDMInitial(website.business_name);
                            navigator.clipboard.writeText(message).then(() => {
                              toast.success('DM copied to clipboard!');
                            }).catch(() => {
                              toast.error('Failed to copy message');
                            });
                          }}
                          className="flex items-center gap-2 text-blue-400 hover:underline text-left"
                        >
                          <Copy className="w-3 h-3" />
                          Copy DM
                        </button>
                        <a 
                          href={`https://tryweb.co.uk/preview/${website.subdomain}?_admin=1`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-primary hover:underline"
                        >
                          <ExternalLink className="w-3 h-3" />
                          tryweb.co.uk/preview/{website.subdomain}
                        </a>
                        <p>{website.page_count} page(s) • Generated {new Date(website.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} at {new Date(website.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>
                        {website.client_email && (
                          <p className="text-primary">Sent to: {website.client_email}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {/* Row 1: Preview - Manage - Change Log */}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            // Open preview page in new tab - with _admin=1 to skip analytics tracking
                            window.open(`https://tryweb.co.uk/preview/${website.subdomain}?_admin=1`, '_blank');
                          }}
                          className="gap-2"
                          data-testid={`preview-${website.id}`}
                        >
                          <Eye className="w-4 h-4" />
                          Preview
                        </Button>
                        {!showArchived && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setManagingWebsite(website);
                              setManageDialog(true);
                            }}
                            className="gap-2"
                            data-testid={`manage-${website.id}`}
                          >
                            <Settings className="w-4 h-4" />
                            Manage
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setChangeLogWebsite(website);
                            setChangeLogDialog(true);
                          }}
                          className="gap-2"
                          disabled={!website.applied_instructions}
                          data-testid={`changelog-${website.id}`}
                        >
                          <FileText className="w-4 h-4" />
                          Change Log
                        </Button>
                      </div>
                      {/* Row 2: Send Preview - Sent via Facebook - Archive */}
                      <div className="flex gap-2 flex-wrap">
                        {!showArchived && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSendingTo(website);
                                setSendEmailDialog(true);
                              }}
                              disabled={website.status === 'live'}
                              className="gap-2"
                              data-testid={`send-${website.id}`}
                            >
                              <Send className="w-4 h-4" />
                              Send Preview
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSentViaFacebook(website)}
                              disabled={website.status === 'live' || website.status === 'preview_sent' || sendingFacebook === website.id}
                              className="gap-2"
                              data-testid={`meta-send-${website.id}`}
                            >
                              {sendingFacebook === website.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Infinity className="w-4 h-4" />
                              )}
                              Sent via Meta
                            </Button>
                          </>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleArchive(website)}
                          disabled={archiving === website.id}
                          className="gap-2"
                          data-testid={`archive-${website.id}`}
                        >
                          {archiving === website.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : showArchived ? (
                            <ArchiveRestore className="w-4 h-4" />
                          ) : (
                            <Archive className="w-4 h-4" />
                          )}
                          {showArchived ? 'Unarchive' : 'Archive'}
                        </Button>
                      </div>
                      {website.status === 'ordered' && !showArchived && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleMakeLive(website.id)}
                            className="gap-2"
                            data-testid={`golive-${website.id}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                            Go Live
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Send Email Dialog */}
      <Dialog open={sendEmailDialog} onOpenChange={setSendEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Website Preview</DialogTitle>
            <DialogDescription>
              Send a preview link to the business owner for {sendingTo?.business_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Client Email Address</Label>
              <Input
                type="email"
                placeholder="client@example.com"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                data-testid="client-email-input"
              />
            </div>
            <Button
              className="w-full"
              onClick={handleSendPreview}
              disabled={sendingEmail || !clientEmail}
              data-testid="send-email-btn"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Preview Email
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Website Dialog */}
      <WebsiteManageDialog
        open={manageDialog}
        onOpenChange={setManageDialog}
        website={managingWebsite}
        onSuccess={fetchWebsites}
      />

      {/* Change Log Dialog */}
      <Dialog open={changeLogDialog} onOpenChange={setChangeLogDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Change Log - {changeLogWebsite?.business_name}
            </DialogTitle>
            <DialogDescription>
              All customizations applied to this website
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {changeLogWebsite?.applied_instructions ? (
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-4 border border-white/10">
                  <h4 className="text-sm font-medium mb-3 text-primary">Applied Customizations</h4>
                  <div className="space-y-2">
                    {changeLogWebsite.applied_instructions.split('.').filter(i => i.trim()).map((instruction, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-primary mt-0.5">•</span>
                        <span className="text-muted-foreground">{instruction.trim()}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  <p>Last updated: {changeLogWebsite?.updated_at ? new Date(changeLogWebsite.updated_at).toLocaleString('en-GB') : 'N/A'}</p>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">No customizations applied yet.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GeneratedWebsites;
